/**
 * @file resolveAttachmentRetentionSentence.ts
 * @description The one sentence at the foot of a file's expanded version strip
 * telling a reader which of these versions will still be there tomorrow.
 * Consumer: `AttachmentList`.
 *
 * It names only the organization's age policy, which is opt-in — hence
 * `number | null` in and `string | null` out, and null is the common case.
 * It deliberately says nothing about the commit-time cap in
 * `SUPERSEDED_SAVES_RETAINED`: that bound is not configurable and not visible,
 * so a sentence about it is noise to everyone who reads this panel. The
 * attribution clause is load-bearing — it tells the reader who can change the
 * number they are looking at.
 *
 * Every clause tracks a filter in `pruneAttachmentSaves`' `agedSaveEntries`,
 * which selects `kind === 'save'` only, skips a version with `share.isShared`,
 * skips the one the attachment's top level mirrors, and — inside the deleting
 * transaction — skips any version a referencing artifact names as its
 * `currentVersionId`. A strip listing a published version and a shared save
 * beside an aged one would otherwise sit under a sentence that is false of two
 * of the three rows.
 */

export const resolveAttachmentRetentionSentence = (maxAgeDays: number | null): string | null => {
    if (maxAgeDays === null || !Number.isFinite(maxAgeDays) || maxAgeDays <= 0) {
        return null;
    }

    const window = maxAgeDays === 1 ? '1 day' : `${maxAgeDays} days`;
    return `Desktop saves older than ${window} are removed, unless one is the current version or carries a share link. Published versions are kept. Set by your organization.`;
};
